import * as React from "react";
import ChevronDownIcon from "lucide-react/dist/esm/icons/chevron-down";
import XIcon from "lucide-react/dist/esm/icons/x";
import { Command as CommandPrimitive } from "cmdk";
import { Badge } from "@quake/shared-ui/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@quake/shared-ui/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@quake/shared-ui/components/ui/popover";
import { cn } from "@quake/shared-ui/lib/utils";

type SelectItem = {
  label: string;
  value: string;
  description?: string;
};

type FancyMultiSelectProps = {
  items: SelectItem[];
  selectedItems: SelectItem[];
  onValueChange?: (items: SelectItem[]) => void;
  disabled?: boolean;
  creatable?: boolean;
  placeholder?: string;
  modal?: boolean;
  onSearchValueChange?: (value: string) => void;
  async?: boolean;
};

export function FancyMultiSelect({
  items,
  selectedItems,
  onValueChange,
  disabled = false,
  creatable = false,
  placeholder: _placeholder,
  onSearchValueChange,
  modal = false,
  async = false,
}: FancyMultiSelectProps) {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<React.ComponentRef<typeof CommandPrimitive.Input>>(null);
  const isUserEditing = React.useRef(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<SelectItem[]>(selectedItems);
  // The latest selection, read synchronously by each edit. React may run a state updater after the handler returns,
  // so a value computed inside one can still be empty when the handler reports it.
  const latestSelectedOptions = React.useRef<SelectItem[]>(selectedItems);
  const [inputValue, setInputValue] = React.useState("");
  const selectedItemsSignature = JSON.stringify(selectedItems); // Deep change detection for prop sync

  const filteredOptions = React.useMemo(() => {
    const selectedValues = new Set(selectedOptions.map((item) => item?.value));
    const trimmed = inputValue.trim().toLowerCase();

    return items.filter((item) => {
      if (item == null) {
        return false;
      }
      if (selectedValues.has(item?.value)) {
        return false;
      }

      if (trimmed) {
        const label = item.label.toLowerCase();
        const value = String(item.value).toLowerCase();
        const description = item.description?.toLowerCase() ?? "";
        return label.includes(trimmed) || value.includes(trimmed) || description.includes(trimmed);
      }

      return true;
    });
  }, [inputValue, items, selectedOptions, async]);

  React.useEffect(() => {
    onSearchValueChange?.(inputValue);
  }, [inputValue, !!onSearchValueChange]);

  // Helper to safely update selected options without fighting incoming prop updates
  const safelyUpdateSelected = React.useCallback(
    (updater: (prev: SelectItem[]) => SelectItem[]) => {
      isUserEditing.current = true;
      const next = updater(latestSelectedOptions.current);
      latestSelectedOptions.current = next;
      setSelectedOptions(next);
      // Allow a short window before re-enabling external prop sync
      setTimeout(() => {
        isUserEditing.current = false;
      }, 100);
      onValueChange?.(next);
    },
    [onValueChange],
  );

  // Memoize create item logic
  const createItemInfo = React.useMemo(() => {
    if (!creatable || !inputValue.trim()) {
      return null;
    }

    const trimmedValue = inputValue.trim();
    const lowerTrimmedValue = trimmedValue.toLowerCase();

    const existsInItems = items.some(
      (item) => item.value.toLowerCase() === lowerTrimmedValue || item.label.toLowerCase() === lowerTrimmedValue,
    );

    const existsInSelected = selectedOptions.some(
      (item) => item.value.toLowerCase() === lowerTrimmedValue || item.label.toLowerCase() === lowerTrimmedValue,
    );

    return !existsInItems && !existsInSelected ? { label: trimmedValue, value: trimmedValue } : null;
  }, [creatable, inputValue, items, selectedOptions]);

  // Memoize placeholder text
  const placeholder = React.useMemo(
    () => _placeholder ?? (creatable ? "Select or create option..." : "Select option..."),
    [creatable, _placeholder],
  );

  const handleUnselect = (option: SelectItem) => {
    safelyUpdateSelected((prev) => prev.filter((s) => s.value !== option.value));
  };

  const selectOption = React.useCallback(
    (item: SelectItem) => {
      safelyUpdateSelected((prev) => [...prev, item]);
      setInputValue("");
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    [safelyUpdateSelected],
  );

  const handleCreateItem = () => {
    if (createItemInfo) {
      selectOption(createItemInfo);
    }
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      requestAnimationFrame(() => inputRef.current?.focus());
    }

    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      safelyUpdateSelected((prev) => prev.slice(0, -1));
    }
  };

  const handleCommandKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      if ((event.key === "Backspace" || event.key === "Delete") && input.value === "") {
        event.preventDefault();
        safelyUpdateSelected((prev) => prev.slice(0, -1));
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setIsOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    },
    [safelyUpdateSelected],
  );

  // Sync selected items with prop changes only when not actively editing
  React.useEffect(() => {
    // Only sync if user is not actively editing (to prevent reset on delete all)
    if (!isUserEditing.current) {
      latestSelectedOptions.current = selectedItems;
      setSelectedOptions(selectedItems);
    }
  }, [selectedItems, selectedItemsSignature]);

  React.useEffect(() => {
    if (!isOpen) {
      setInputValue("");
      return;
    }

    requestAnimationFrame(() => inputRef.current?.focus());
  }, [isOpen]);

  const handleOpenChange = (next: boolean) => {
    if (disabled) {
      return;
    }

    setIsOpen(next);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={isOpen}
          aria-disabled={disabled}
          aria-haspopup="listbox"
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            "flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "ring-offset-background",
            disabled && "cursor-not-allowed opacity-50",
          )}
        >
          <div className="flex flex-1 flex-wrap items-center gap-1">
            {selectedOptions
              .filter((option) => !!option)
              .map((option) => (
                <Badge key={option.value} variant="secondary" className="flex items-center gap-1">
                  {option.label}
                  <button
                    type="button"
                    disabled={disabled}
                    className="rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleUnselect(option);
                      }
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleUnselect(option);
                    }}
                  >
                    <XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </Badge>
              ))}

            {selectedOptions.length === 0 ? <span className="text-muted-foreground">{placeholder}</span> : null}
          </div>
          <ChevronDownIcon
            className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-180")}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-0" sideOffset={4}>
        <Command shouldFilter={false} onKeyDown={handleCommandKeyDown} className="w-full">
          <CommandInput
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            placeholder={placeholder}
            disabled={disabled}
          />
          <CommandList>
            {filteredOptions.length ? (
              <CommandGroup>
                {filteredOptions.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onSelect={() => selectOption(item)}
                    className="cursor-pointer"
                  >
                    <div className="flex flex-col text-left">
                      <span>{item.label}</span>
                      {item.description ? (
                        <span className="text-xs text-muted-foreground">{item.description}</span>
                      ) : null}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              <CommandEmpty className="px-3 py-4 text-left text-xs text-muted-foreground">
                {!createItemInfo && !creatable ? "No results found." : "Start typing to add options."}
              </CommandEmpty>
            )}

            {createItemInfo ? (
              <CommandItem
                key={`create-${createItemInfo.value}`}
                value={`create-${createItemInfo.value}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onSelect={handleCreateItem}
                className="cursor-pointer border-t border-border/50"
              >
                <div className="flex flex-col text-left">
                  <span className="font-medium">Create "{createItemInfo.label}"</span>
                  <span className="text-xs text-muted-foreground">Press Enter to create</span>
                </div>
              </CommandItem>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
